import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Fragment, type ReactNode } from "react";

import { StyledAnchor } from "@/components/common/styles";
import { newTabProps } from "@/components/marketing/marketingLinks";
import { BODY, HEADING_2 } from "@/components/marketing/marketingType";
import {
  ACCENT,
  ACCENT_DEEP,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import {
  LandingButton,
  LandingImage,
  LandingSection,
} from "../LandingPrimitives";
import type { Cards } from "./cardsShared";
import { Lines } from "@/components/marketing/Lines";

/**
 * `.compare-section_wrap`: the band's column, ruled down both sides in the
 * outline dash until the wide step. Its control is `.button_new.is_hug`,
 * which keeps its own width until the tiny step stretches every child.
 */
const WRAP = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
  borderLeft: `1px dashed ${marketingPalette.outline}`,
  borderRight: `1px dashed ${marketingPalette.outline}`,
  "& > a, & > button": { [MID]: { width: "auto" }, [TINY]: { width: 1 } },
  [NOT_WIDE]: { border: "none" },
  [TINY]: { alignItems: "stretch" },
} satisfies SxProps<Theme>;

/** `.compare-section-title_wrap`: 70% of the column, then the whole of it. */
const TITLE_WRAP = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "70%",
  textAlign: "center",
  [NOT_WIDE]: { width: 1 },
} satisfies SxProps<Theme>;

/** `.compare-section_box`: the tinted box, capped at 700 until the wide step. */
const BOX = {
  display: "flex",
  flexDirection: "column",
  width: 1,
  maxWidth: "700px",
  p: "12px",
  borderRadius: "16px",
  backgroundColor: marketingPalette.surface,
  [NOT_WIDE]: { maxWidth: "none" },
} satisfies SxProps<Theme>;

/** `.compare-section-list_wrap`: one cell per product, read across. */
const ROW = {
  display: "grid",
  gap: "10.08px",
  gridAutoColumns: "minmax(0, 1fr)",
  gridAutoFlow: "column",
} satisfies SxProps<Theme>;

/** `.with-paddings`: every row but the first is set 12 off the rule above it. */
const PADDED_ROW = { py: "12px" } satisfies SxProps<Theme>;

/** `.compare-proxy-type_wrap`: the product's own card at the head of its column. */
const HEAD_CELL = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  p: "16px 32px",
  borderRadius: "16px",
  backgroundColor: "common.white",
  boxShadow: "rgba(202, 207, 230, 0.9) 0px 14px 27px -18px",
  [TINY]: { p: "12.8px", textAlign: "center" },
} satisfies SxProps<Theme>;

const HEAD_ICON = {
  width: "48px",
  height: "48px",
  [TINY]: { width: "35.2px", height: "35.2px" },
} satisfies SxProps<Theme>;

/** `.compare-section_title`: the product name, a hair heavier than its weight. */
const HEAD_TITLE = {
  fontSize: 18.08,
  lineHeight: "21.7px",
  WebkitTextStrokeWidth: "0.3px",
  [TINY]: { fontSize: 16, lineHeight: "19.2px" },
} satisfies SxProps<Theme>;

/** `.compare-section_lbl`: the "Currently Viewing" marker. */
const MARKER = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: "12px",
  height: "32px",
  borderRadius: "8px",
  backgroundColor: marketingPalette.compareMarker,
  fontSize: 13,
  lineHeight: "19.5px",
  [TINY]: { textAlign: "center", lineHeight: "15px" },
} satisfies SxProps<Theme>;

/**
 * `.compare-section_link`: the accent "Learn More" line, which washes and
 * deepens under the pointer, and takes a wash of its own from the card step.
 * The washes are the accent at published alphas.
 */
const LINK = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  height: "32px",
  pl: 0,
  pr: "8px",
  borderRadius: "8px",
  color: ACCENT,
  fontSize: 16,
  lineHeight: "24px",
  transition: "all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
  "&:hover": {
    gap: "12.8px",
    px: "12.8px",
    color: ACCENT_DEEP,
    backgroundColor: "rgba(26, 179, 148, 0.1)",
  },
  [NOT_WIDE]: { height: "48px", px: "16px", backgroundColor: "common.white" },
  [MID]: { height: "32px", backgroundColor: "rgba(26, 179, 148, 0.1)" },
  [TINY]: {
    width: 1,
    px: "8px",
    color: ACCENT_DEEP,
    backgroundColor: "rgba(26, 179, 148, 0.27)",
    fontSize: 14,
    lineHeight: "21px",
    "&:hover": { px: "8px" },
  },
} satisfies SxProps<Theme>;

/** `.compare-section_btn`: the navy "Buy Now", which greys under the pointer and grows from the wide step. */
const BUY = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  p: "8px 20px 8px 17.6px",
  borderRadius: "8px",
  backgroundColor: marketingPalette.darkGround,
  color: "common.white",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  "&:hover": { backgroundColor: marketingPalette.inkSubtle },
  [NOT_WIDE]: { p: "12px 32px" },
} satisfies SxProps<Theme>;

/** `.compare-section_button-list`: marker, link and control stacked, and stretched on a phone. */
const CONTROLS = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  [TINY]: { alignItems: "stretch" },
} satisfies SxProps<Theme>;

/** `.compare-section-txt_wrap`: one line of the feature rows. */
const FEATURE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 500,
  [TINY]: { textAlign: "center" },
} satisfies SxProps<Theme>;

/** `.compare-section_line`: a hairline that fades out to both ends. */
function Rule() {
  return (
    <Box
      sx={{
        height: "1.75px",
        opacity: 0.12,
        // The published gradient's own grey; it is drawn at 12% and nowhere else.
        backgroundImage:
          "linear-gradient(260deg, rgba(41, 41, 49, 0), rgb(41, 41, 49) 50%, rgba(41, 41, 49, 0))",
      }}
    />
  );
}

function HeadCell({ card }: { card: Card }) {
  return (
    <Box sx={HEAD_CELL}>
      {card.icon ? <LandingImage src={card.icon} sx={HEAD_ICON} /> : null}
      <Box sx={HEAD_TITLE}>{card.title}</Box>
    </Box>
  );
}

/** `.section-compare-from_wrap`: "From", the figure, and its unit on one line. */
function PriceCell({ card }: { card: Card }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6.4px",
      }}
    >
      {card.priceLead ? (
        <Box sx={{ fontSize: 12, lineHeight: "18px", opacity: 0.5 }}>
          {card.priceLead}
        </Box>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "4.8px" }}>
        <Box sx={{ fontSize: 16.96, fontWeight: 500, lineHeight: "20.35px" }}>
          {card.price}
        </Box>
        {card.per ? (
          <Box sx={{ fontSize: 10.08, lineHeight: "17.44px", opacity: 0.6 }}>
            {card.per}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function ControlCell({ card }: { card: Card }) {
  return (
    <Box sx={CONTROLS}>
      {card.label ? <Box sx={MARKER}>{card.label}</Box> : null}
      {card.href ? (
        <StyledAnchor href={card.href} {...newTabProps(card)} sx={LINK}>
          Learn More
        </StyledAnchor>
      ) : null}
      {card.cta ? (
        <StyledAnchor href={card.cta.href} {...newTabProps(card.cta)} sx={BUY}>
          {card.cta.label}
        </StyledAnchor>
      ) : null}
    </Box>
  );
}

/** The rows of the box: heads, prices, controls, then one ruled row per feature line. */
function CompareBox({ block }: { block: Cards }) {
  const depth = Math.max(
    ...block.items.map((card) => card.bullets?.length ?? 0)
  );
  const across = (draw: (card: Card) => ReactNode) =>
    block.items.map((card) => (
      <Fragment key={card.title}>{draw(card)}</Fragment>
    ));
  return (
    <Box sx={BOX}>
      <Box sx={ROW}>
        {across((card) => (
          <HeadCell card={card} />
        ))}
      </Box>
      <Box sx={[ROW, PADDED_ROW]}>
        {across((card) => (
          <PriceCell card={card} />
        ))}
      </Box>
      <Rule />
      <Box sx={[ROW, PADDED_ROW]}>
        {across((card) => (
          <ControlCell card={card} />
        ))}
      </Box>
      {Array.from({ length: depth }, (_, row) => (
        <Fragment key={row}>
          <Rule />
          <Box sx={[ROW, PADDED_ROW]}>
            {across((card) => (
              <Box sx={FEATURE}>{card.bullets?.[row] ?? ""}</Box>
            ))}
          </Box>
        </Fragment>
      ))}
    </Box>
  );
}

/**
 * "Compare Our Products": two columns read across, not two cards read down.
 * `.container_new.is-top_padding`: the section opens on the break and closes
 * on its control, so the card after it carries the next break itself.
 */
export default function CardsCompare({ block }: { block: Cards }) {
  return (
    <LandingSection padding="top">
      <Box sx={WRAP}>
        <Box sx={TITLE_WRAP}>
          <Typography component="h2" sx={HEADING_2}>
            <Lines text={block.title} />
          </Typography>
          {block.body ? <Typography sx={BODY}>{block.body}</Typography> : null}
        </Box>
        <CompareBox block={block} />
        {block.cta ? <LandingButton cta={block.cta} /> : null}
      </Box>
    </LandingSection>
  );
}
